import React, { createContext, PropsWithChildren, useState, useContext, useEffect, useCallback } from 'react';

export interface TranslationBundle {
  [key: string]: string;
}

export interface IAppConfig {
  _id: string;
  UIConfig: {
    overlayTitle: string;
    overlayText: string;
    headerImage: any;
    dialogTitle: string;
    privacySectionTitle: string;
    privacySectionText: string;
    acceptButtonText: string;
    settingsButtonText: string;
    saveSettingsButtonText: string;
    backgroundColor: string;
    textFontColor: string;
    buttonFontColor: string;
    buttonBackgroundColor: string;
  };
  cookiesConfigs: {
    type: string;
    description: string;
    optional: boolean;
    cookies: {
      name: string;
      host: string;
      duration: string;
    }[];
  }[];
  translationBundle: TranslationBundle
}

export interface ICookiesConfig {
  [key: string]: boolean;
}

export interface AppContextType {
  showApp: boolean;
  setShowApp(state: any): void;
  isFetching: boolean;
  setIsFetching(state: any): boolean;
  appConfig: IAppConfig;
  setAppConfig(state: any): void;
  openDialog: boolean;
  setOpenDialog(state: any): void;
  showBottomPanel: boolean;
  setShowBottomPanel(state: any): void;
  showVendors: number;
  setShowVendors(state: any): void;
  cookiesConfig: ICookiesConfig;
  setCookiesConfig(state: any): void;
  getTranslation(key: string, args?: any[]): string;
}

function getCookiesConfig(): ICookiesConfig {
  let configs = {};

  try {
    configs = JSON.parse(localStorage.getItem('sydle_one_lgpd_config')!);
  } catch(_) {}

  return configs;
}

const AppContext = createContext<AppContextType>(null!);

function GlobalState({ children }: PropsWithChildren<{}>) {
  const [showApp, setShowApp] = useState<boolean>(!localStorage.getItem('sydle_one_lgpd_closed'));
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [showBottomPanel, setShowBottomPanel] = useState<boolean>(true);
  const [showVendors, setShowVendors] = useState<number>(-1);
  const [appConfig, setAppConfig] = useState<IAppConfig>(null!);
  const [cookiesConfig, setCookiesConfig] = useState<ICookiesConfig>(getCookiesConfig());
  const [translationBundle, setTranslationBundle] = useState<TranslationBundle>({});

  const getTranslation = useCallback((key: string, args?: any[]) => {
    let unit = translationBundle[key];
    if (unit && args && args.length) {
      args.forEach((param, index) => {
        unit = unit.replace(`{${index}}`, param.replace(/\$/gi, '$$$$'));
      });
    }
    return unit || key;
  }, [translationBundle]);

  useEffect(() => {
    const xhr = new XMLHttpRequest();

    xhr.addEventListener('readystatechange', function() {
      if (this.readyState === 4) {
        if (this.status === 200) {
          try {
            const config: IAppConfig = JSON.parse(this.responseText);
            setAppConfig(config);
            setTranslationBundle(config.translationBundle);
          } catch(e) {}
          setIsFetching(false);
        } else {
          setIsFetching(false);
        }
      }
    });

    xhr.open('GET', `${window.SYDLE_LGPD_ENV?.REACT_APP_BASE_URL}/api/1/${window.SYDLE_LGPD_ENV?.REACT_APP_APPLICATION}/_classId/5f63788bbce7a3412b7cf363/getInstance/${window.SYDLE_LGPD_ENV?.REACT_APP_ID}`);

    if (window.SYDLE_LGPD_ENV?.REACT_APP_ORGANIZATION) {
      xhr.setRequestHeader('X-Explorer-Account-Token', window.SYDLE_LGPD_ENV?.REACT_APP_ORGANIZATION);
      xhr.setRequestHeader('Accept-Language', window.SYDLE_LGPD_ENV?.REACT_APP_LANGUAGE || navigator.language)
    }

    xhr.send();
  }, []);

  const context = {
    showApp,
    setShowApp,
    isFetching,
    setIsFetching,
    appConfig,
    setAppConfig,
    openDialog,
    setOpenDialog,
    showBottomPanel,
    setShowBottomPanel,
    showVendors,
    setShowVendors,
    cookiesConfig,
    setCookiesConfig,
    getTranslation
  } as AppContextType;

  return (
    <AppContext.Provider value={context}>
      {children}
    </AppContext.Provider>
  );
}

export function useAppState() {
  const context = useContext<AppContextType>(AppContext);
  if (context === undefined) {
    throw new Error('useAppState must be used within a AppContext');
  }
  return context;
}

export default GlobalState;